// Shared realtime WebSocket client that dispatches events across the app.
// Provides a singleton connection, event subscriptions, and cached payload delivery.
// Usage:
//   import realtimeClient from './realtime/client';
//   const unsubscribe = realtimeClient.subscribe('today_queue', (payload) => {
//       console.log(payload);
//   });
//   // later: unsubscribe();
import {
  ACCESS_TOKEN_CHANGED_EVENT,
  AuthRefreshTerminalError,
  AuthRefreshTransientError,
  AuthSessionExpiredError,
  ensureValidAccessToken,
  getAccessToken,
  isTokenExpired,
} from "../api/client";

type RealtimeHandler = (payload: unknown) => void;

interface SubscribeOptions {
  emitCachedPayload?: boolean;
}

interface RealtimeClientOptions {
  listenToAuthChanges?: boolean;
}

type UrlFactory = () => string | null;

const DEFAULT_WS_PATH = "/ws/clinic-realtime/";
const INITIAL_RECONNECT_DELAY_MS = 1500;
const MAX_RECONNECT_DELAY_MS = 60_000;
const AUTH_CLOSE_CODE = 4401;
const FORBIDDEN_CLOSE_CODE = 4403;
const ENV_WS_URL = import.meta.env?.VITE_WS_API_URL;
const NON_CACHEABLE_EVENT_TYPES = new Set([
  "new_queue",
  "re_print_queue",
  "patient_sticker_print",
  "medicine_sticker_print",
  "open_cash_drawer",
]);

const appendAccessTokenToUrl = (
  url: string | null,
  accessToken: string | null,
): string | null => {
  if (!url || !accessToken) {
    return url;
  }

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}access_token=${encodeURIComponent(accessToken)}`;
};

const DEFAULT_URL_FACTORY: UrlFactory = () => {
  if (ENV_WS_URL) {
    return ENV_WS_URL;
  }
  if (typeof window === "undefined") {
    return null;
  }
  const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
  const host = window.location.host;
  return `${wsScheme}://${host}${DEFAULT_WS_PATH}`;
};

export class RealtimeClient {
  private urlFactory: UrlFactory;
  private socket: WebSocket | null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null;
  private handlers: Map<string, Set<RealtimeHandler>>;
  private lastPayloadByType: Map<string, unknown>;
  private connectionAttempted: boolean;
  private shouldReconnect: boolean;
  private reconnectAttempts: number;
  private authRecoveryAttempted: boolean;
  private connectionGeneration: number;

  constructor(
    urlFactory: UrlFactory = DEFAULT_URL_FACTORY,
    options: RealtimeClientOptions = {},
  ) {
    const { listenToAuthChanges = false } = options;

    this.urlFactory = urlFactory;
    this.socket = null;
    this.reconnectTimer = null;
    this.handlers = new Map();
    this.lastPayloadByType = new Map();
    this.connectionAttempted = false;
    this.shouldReconnect = false;
    this.reconnectAttempts = 0;
    this.authRecoveryAttempted = false;
    this.connectionGeneration = 0;

    if (listenToAuthChanges && typeof window !== "undefined") {
      window.addEventListener(ACCESS_TOKEN_CHANGED_EVENT, (event) => {
        this._handleAccessTokenChanged(event as CustomEvent);
      });
    }
  }

  subscribe<TPayload = unknown>(
    eventType: string,
    handler: (payload: TPayload) => void,
    options: SubscribeOptions = {},
  ): () => void {
    if (!eventType || typeof handler !== "function") return () => {};
    const { emitCachedPayload = true } = options;

    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    const realtimeHandler = handler as RealtimeHandler;
    this.handlers.get(eventType)!.add(realtimeHandler);

    this._ensureConnection();
    if (emitCachedPayload) {
      this._emitCachedPayload(eventType, realtimeHandler);
    }

    return () => {
      this.unsubscribe(eventType, realtimeHandler);
    };
  }

  unsubscribe(eventType: string, handler: RealtimeHandler): void {
    const handlerSet = this.handlers.get(eventType);
    if (!handlerSet) return;
    handlerSet.delete(handler);
    if (handlerSet.size === 0) {
      this.handlers.delete(eventType);
    }
    if (this.handlers.size === 0) {
      this.disconnect();
    }
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.connectionGeneration += 1;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.connectionAttempted = false;
    this.reconnectAttempts = 0;
    this.authRecoveryAttempted = false;

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  reconnect(resetAuthRecovery = true): void {
    this.connectionGeneration += 1;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.connectionAttempted = false;
    this.reconnectAttempts = 0;
    if (resetAuthRecovery) {
      this.authRecoveryAttempted = false;
    }

    const currentSocket = this.socket;
    if (currentSocket) {
      currentSocket.onopen = null;
      currentSocket.onmessage = null;
      currentSocket.onclose = null;
      currentSocket.onerror = null;
      this.socket = null;
      currentSocket.close();
    }

    if (this.handlers.size === 0) {
      this.shouldReconnect = false;
      return;
    }

    this.shouldReconnect = true;
    this._ensureConnection();
  }

  private _ensureConnection(forceRefresh = false): void {
    if (this.socket || this.connectionAttempted) return;
    const accessToken = getAccessToken();

    if (!forceRefresh && accessToken && !isTokenExpired(accessToken)) {
      this.shouldReconnect = true;
      this._openSocket(accessToken, this.connectionGeneration);
      return;
    }

    this.connectionAttempted = true;
    this.shouldReconnect = true;
    const generation = this.connectionGeneration;

    ensureValidAccessToken(forceRefresh)
      .then((token) => {
        if (
          generation !== this.connectionGeneration ||
          !this.shouldReconnect ||
          this.handlers.size === 0
        ) {
          return;
        }
        this._openSocket(token, generation);
      })
      .catch((error: unknown) => {
        if (generation !== this.connectionGeneration) {
          return;
        }
        this.connectionAttempted = false;

        if (error instanceof AuthSessionExpiredError) {
          this.shouldReconnect = false;
          console.warn(
            "RealtimeClient: authentication expired, reconnect stopped",
          );
          return;
        }

        if (error instanceof AuthRefreshTerminalError) {
          this.shouldReconnect = false;
          console.warn(
            "RealtimeClient: token refresh failed terminally, reconnect stopped",
          );
          return;
        }

        if (error instanceof AuthRefreshTransientError) {
          console.warn(
            "RealtimeClient: token refresh temporarily unavailable, will reconnect",
          );
        } else {
          console.error("RealtimeClient: failed to prepare access token", error);
        }
        this._scheduleReconnect();
      });
  }

  private _openSocket(accessToken: string, generation: number): void {
    if (
      generation !== this.connectionGeneration ||
      this.socket ||
      !this.shouldReconnect
    ) {
      return;
    }

    const url = appendAccessTokenToUrl(this.urlFactory(), accessToken);
    if (!url) {
      this.connectionAttempted = false;
      return;
    }

    this.connectionAttempted = true;
    try {
      this.socket = new WebSocket(url);
      this._attachSocketHandlers();
    } catch (err) {
      console.error("RealtimeClient: failed to create WebSocket", err);
      this._scheduleReconnect();
    }
  }

  private _attachSocketHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      this.connectionAttempted = false;
    };

    this.socket.onmessage = (event) => {
      this.reconnectAttempts = 0;
      this.authRecoveryAttempted = false;
      this._handleMessage(event.data);
    };

    this.socket.onclose = (event) => {
      if (this.shouldReconnect && this.handlers.size > 0) {
        console.warn("RealtimeClient: connection closed, will reconnect");
      }
      this.socket = null;
      this.connectionAttempted = false;

      if (event?.code === FORBIDDEN_CLOSE_CODE) {
        this.shouldReconnect = false;
        console.warn(
          "RealtimeClient: authorization rejected, reconnect stopped",
        );
        return;
      }

      if (event?.code === AUTH_CLOSE_CODE) {
        if (this.authRecoveryAttempted) {
          this.shouldReconnect = false;
          console.warn(
            "RealtimeClient: authentication rejected after refresh, reconnect stopped",
          );
          return;
        }

        this.authRecoveryAttempted = true;
        this._ensureConnection(true);
        return;
      }

      if (this.shouldReconnect && this.handlers.size > 0) {
        this._scheduleReconnect();
      }
    };

    this.socket.onerror = (err) => {
      console.error("RealtimeClient: socket error", err);
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.close();
      }
    };
  }

  private _handleMessage(rawData: string): void {
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(rawData);
    } catch (err) {
      console.error("RealtimeClient: invalid JSON message", err);
      return;
    }

    const { type } = payload || {};
    if (!type || typeof type !== "string") {
      return;
    }

    if (!NON_CACHEABLE_EVENT_TYPES.has(type)) {
      if (type === "today_queue" && (payload?.data as Record<string, unknown>)?.type === "queue_item_updated") {
        const cached = this.lastPayloadByType.get("today_queue") as Record<string, any> | undefined;
        if (cached?.data?.queues && Array.isArray(cached.data.queues)) {
          const updatedItem = (payload.data as Record<string, any>).item;
          if (updatedItem?.id) {
            cached.data.queues = cached.data.queues.map((q: any) =>
              q.id === updatedItem.id ? { ...q, ...updatedItem } : q
            );
          }
        }
      } else {
        this.lastPayloadByType.set(type, payload);
      }
    }

    const handlerSet = this.handlers.get(type);
    if (!handlerSet || handlerSet.size === 0) {
      return;
    }
    handlerSet.forEach((handler) => {
      try {
        handler(payload);
      } catch (err) {
        console.error("RealtimeClient: handler error", err);
      }
    });
  }

  clearPayloadCache(eventType: string) {
    if (eventType) {
      this.lastPayloadByType.delete(eventType);
    } else {
      this.lastPayloadByType.clear();
    }
  }

  private _emitCachedPayload(
    eventType: string,
    handler: RealtimeHandler,
  ): void {
    if (!this.lastPayloadByType.has(eventType)) {
      return;
    }
    const payload = this.lastPayloadByType.get(eventType);
    const invoke = () => {
      try {
        handler(payload);
      } catch (err) {
        console.error("RealtimeClient: handler error", err);
      }
    };

    if (typeof queueMicrotask === "function") {
      queueMicrotask(invoke);
    } else {
      setTimeout(invoke, 0);
    }
  }

  private _scheduleReconnect(): void {
    if (
      this.reconnectTimer ||
      !this.shouldReconnect ||
      this.handlers.size === 0
    )
      return;
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts,
      MAX_RECONNECT_DELAY_MS,
    );
    this.reconnectAttempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connectionAttempted = false;
      this._ensureConnection();
    }, delay);
  }

  private _handleAccessTokenChanged(event: CustomEvent): void {
    const token = event?.detail?.token ?? getAccessToken();

    if (!token) {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.disconnect();
      return;
    }

    if (this.handlers.size > 0) {
      this.reconnect(false);
    }
  }
}

const realtimeClient = new RealtimeClient(DEFAULT_URL_FACTORY, {
  listenToAuthChanges: true,
});

export default realtimeClient;
